import { useEffect, useState } from 'react';

/**
 * The accountability ring.
 *
 * The one piece of expressive design in the product, and it is earned: it is
 * the mark (`experience-system.md` §4.1) and the live progress indicator at the
 * same time. docs/design-dashboard.md §7.2.
 *
 * Three rules it obeys:
 *
 * - **It never loops.** It is progress, not activity. A looping indicator on a
 *   live surface is the thing every competitor does and it is wrong — it
 *   implies the system is busy rather than that people are answering.
 * - **It steps under `prefers-reduced-motion`** rather than animating, because
 *   the information is the number and the animation is only how it got there.
 * - **The number lives inside it**, tabular, and is the largest thing on the
 *   screen it appears on.
 *
 * Never red. Red is a band and a dot (G12); a ring that filled red would make
 * the most-looked-at object on the live screen the one thing the brand book
 * says must never be a field.
 */
export function Ring({
  safe,
  total,
  label = 'accounted for',
}: {
  safe: number;
  total: number;
  label?: string;
}) {
  const fraction = total > 0 ? Math.min(1, safe / total) : 0;

  // 480ms — `duration.deliberate`, and the only thing permitted to exceed
  // `base` on a live surface.
  const [drawn, setDrawn] = useState(fraction);
  useEffect(() => {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) {
      setDrawn(fraction);
      return;
    }
    const id = requestAnimationFrame(() => setDrawn(fraction));
    return () => cancelAnimationFrame(id);
  }, [fraction]);

  const size = 260;
  const stroke = 14;
  const r = (size - stroke) / 2;
  const circumference = 2 * Math.PI * r;

  return (
    <div className="relative inline-flex items-center justify-center">
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        role="img"
        aria-label={`${safe} of ${total} ${label}`}
      >
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="var(--line-2)"
          strokeWidth={stroke}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="var(--safe)"
          strokeWidth={stroke}
          strokeLinecap="butt"
          strokeDasharray={circumference}
          strokeDashoffset={circumference * (1 - drawn)}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{
            transition: 'stroke-dashoffset var(--duration-deliberate) var(--ease-standard)',
          }}
        />
      </svg>

      <div className="absolute flex flex-col items-center">
        <div className="tabular text-[56px] leading-none font-[650]">{safe}</div>
        <div className="tabular mt-1 text-[17px] text-[var(--ink-2)]">of {total}</div>
        <div className="mt-2 text-[11px] font-medium tracking-[0.06em] text-[var(--ink-2)] uppercase">
          {label}
        </div>
      </div>
    </div>
  );
}
